import {
  GridRowParams,
  getGridStringOperators,
  GridActionsCell,
  GridActionsCellItem,
  GridColDef,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useRef, useState } from 'react';
import { BasicInfo, RuleModel } from '../../../../types';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { RulesKeys, listAllRules } from '../../../api/rules-engine-api';
import DeleteRuleDialog from './delete-rule-dialog';
import DisableRuleDialog from './disable-rule-dialog';
import EnableRuleDialog from './enable-rule-dialog';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  RulesEngineRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import AvatarChip from '../../../shared/avatar-chip';
import { getAllPoliciesQuery } from '../../../api/users-api';
import { triggerEventOptions } from './trigger-event-options';
import { pagesConfig } from '../../../shared/pages-config';
import { actionOptions } from './action-options';
import RulesEngineLogsDialog from './rules-engine-logs-dialog';
import RulesEngineAllActionLogsDialog from './rules-engine-all-action-logs-dialog';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import RulesEngineDrawerDetails from './rules-engine-drawer-details';
import EditRuleDialog from './edit-rule-dialog';
import { getRuleRunLogsCount } from '../../../api/rules-engine-api';
import StatusLabel, { StatusType } from '../../../shared/status-label';
import CreateButtonGroup from '../../../shared/buttons/create-button-group';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import { migrateRuleToConditionBlocks } from './rules-engine-form-utils';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import RulesEngineDrawerDetailsOld from './rules-engine-drawer-details-old';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Delete from '@mui/icons-material/Delete';
import History from '@mui/icons-material/History';
import Settings from '@mui/icons-material/Settings';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import Edit from '@mui/icons-material/Edit';
import LibraryAdd from '@mui/icons-material/LibraryAdd';

function CreateRuleDropdownButton({
  disabled,
  navigate,
  tooltipText,
}: {
  disabled: boolean;
  navigate: ReturnType<typeof useNavigate>;
  tooltipText?: string;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleToggle = () => {
    setAnchorEl((prev) => (prev ? null : anchorRef.current));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Tooltip arrow title={tooltipText}>
      <span>
        <CreateButtonGroup
          anchorRef={anchorRef}
          disabled={disabled}
          onClick={() => {
            navigate(
              `/${pagesConfig.rulesEngine.link}/${pagesConfig.createRule.link}`
            );
          }}
          label="Create Rule"
          handleToggle={handleToggle}
        />
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              navigate(
                `/${pagesConfig.rulesEngine.link}/${pagesConfig.createRule.link}`
              );
            }}
          >
            Create Rule
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              navigate(
                `/${pagesConfig.rulesEngine.link}/${pagesConfig.browseTemplates.link}`
              );
            }}
          >
            Browse Templates
          </MenuItem>
        </Menu>
      </span>
    </Tooltip>
  );
}

export default function RulesEngineGrid() {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDeleteRuleDialog, setOpenDeleteRuleDialog] = useState(false);
  const [openDisableRuleDialog, setOpenDisableRuleDialog] = useState(false);
  const [openEnableRuleDialog, setOpenEnableRuleDialog] = useState(false);

  const [openEditRuleDialog, setOpenEditRuleDialog] = useState(false);
  const [openRuleLogsDialog, setOpenRuleLogsDialog] = useState(false);
  const [openActionLogsDialog, setOpenActionLogsDialog] = useState(false);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [ruleForDialog, setRuleForDialog] = useState<RuleModel | null>(null);

  const { changeLogRoles, rulesEngineRoles, viewType, features } =
    useUserSettings();
  const uiNewFeatureEnabled = isFeatureEnabled(
    'rulesEnginev2UiReact',
    features
  );

  const { isLoading, data, isFetching } = useQuery({
    queryKey: [RulesKeys.RulesList],
    queryFn: () => listAllRules(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('rules'),
    },
  });

  const ruleIdsToCount = useMemo(() => {
    if (!data) return [];
    return data.filter((r) => r.basicInfo.status !== 'Draft').map((r) => r.id);
  }, [data]);

  const { countsData, isPendingCounts } = useQueries({
    queries: ruleIdsToCount.map((ruleId) => ({
      queryKey: [RulesKeys.RunLogs, 'count', ruleId],
      queryFn: () => getRuleRunLogsCount(ruleId),
      enabled: !!ruleId,
    })),
    combine: (results) => {
      const counts: Record<
        number,
        { total: number; successful: number; failed: number }
      > = {};

      results.forEach((result, index) => {
        const ruleId = ruleIdsToCount[index];
        const data = result.data || { total: 0, successful: 0, failed: 0 };
        counts[ruleId] = data;
      });

      return {
        countsData: counts,
        isPendingCounts: results.some((query) => query.isPending),
      };
    },
  });

  const { data: allPolicies, isLoading: isLoadingPolicies } = useQuery(
    getAllPoliciesQuery()
  );

  const selectedRuleIdFromUrl = searchParams.get('selectedRule');
  const selectedRuleFromUrl = data?.find(
    (r) => r.id.toString() === selectedRuleIdFromUrl
  );

  const selectedRuleForDrawer = useMemo(() => {
    if (!selectedRuleFromUrl) {
      return null;
    }
    return migrateRuleToConditionBlocks(selectedRuleFromUrl);
  }, [selectedRuleFromUrl]);

  const openRulesDetailsDrawer = !!selectedRuleFromUrl && !isLoading;

  const handleCloseDrawer = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('selectedRule');
      return prev;
    });
  }, [setSearchParams]);

  const updateSelectedRuleStatus = (status: 'Enabled' | 'Disabled') => {
    if (selectedRuleFromUrl) {
      const newRule = {
        ...selectedRuleFromUrl,
        basicInfo: { ...selectedRuleFromUrl.basicInfo, status },
      };
      // Also update the query cache for the main grid
      queryClient.setQueryData(
        [RulesKeys.RulesList],
        (oldData: RuleModel[] | undefined) =>
          oldData
            ? oldData.map((r) => (r.id === newRule.id ? newRule : r))
            : undefined
      );
    }
  };

  const handleEnableRule = useCallback((rule?: RuleModel) => {
    setRuleForDialog(rule || null);
    setOpenEnableRuleDialog(true);
  }, []);

  const handleDisableRule = useCallback((rule?: RuleModel) => {
    setRuleForDialog(rule || null);
    setOpenDisableRuleDialog(true);
  }, []);

  const handleDeleteRule = useCallback((rule?: RuleModel) => {
    setRuleForDialog(rule || null);
    setOpenDeleteRuleDialog(true);
  }, []);

  const handleEditRule = useCallback(
    (rule: RuleModel) => {
      if (rule.basicInfo.status === 'Draft') {
        navigate(`/${pagesConfig.rulesEngine.link}/edit/${rule.id}`);
      } else {
        setRuleForDialog(rule);
        setOpenEditRuleDialog(true);
      }
    },
    [navigate]
  );

  const handleCloneRule = useCallback(
    (rule: RuleModel) => {
      handleCloseDrawer();
      navigate(
        `/${pagesConfig.rulesEngine.link}/${pagesConfig.cloneRule.link}/${rule.id}`
      );
    },
    [handleCloseDrawer, navigate]
  );

  const onEditExisting = () => {
    setOpenEditRuleDialog(false);
    if (ruleForDialog) {
      navigate(`/${pagesConfig.rulesEngine.link}/edit/${ruleForDialog.id}`);
    }
  };

  const handleOpenLogs = useCallback((rule: RuleModel) => {
    setRuleForDialog(rule);
    setOpenRuleLogsDialog(true);
  }, []);

  const handleRowClick = (params: GridRowParams<RuleModel>) => {
    if (isAbleToViewDetails) {
      setSearchParams((prev) => {
        prev.set('selectedRule', params.row.id.toString());
        return prev;
      });
      createEvent(analytics.automation.rules_engine.viewed_rule_details, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.rulesEngine,
      });
    }
  };

  const selectedRulesFromGrid =
    data?.filter((rule) => {
      return selectedRows.includes(rule.id);
    }) || [];

  const columns: GridColDef<RuleModel>[] = useMemo(() => {
    const baseColumns: GridColDef<RuleModel>[] = [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 180,
        type: 'date',
        display: 'flex',
        valueGetter: (_params, row) => {
          return new Date(row.basicInfo.createdDate);
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'name',
        headerName: 'Rule Name',
        flex: 1,
        display: 'flex',

        valueGetter: (_params: BasicInfo, row) => {
          return row.basicInfo.ruleName;
        },
      },
      {
        field: 'triggerInfo',
        headerName: 'Trigger Event',
        display: 'flex',

        width: 180,
        valueGetter: (_params: BasicInfo, row) => {
          const triggerEvent = triggerEventOptions.find(
            (option) => option.value === row.triggerInfo.triggerEvent
          );
          return triggerEvent?.displayValue;
        },
      },
      {
        field: 'triggerActions',
        headerName: 'Trigger Actions',
        flex: 2,
        display: 'flex',
        filterOperators: getGridStringOperators().filter(
          (op) => op.value === 'contains'
        ),
        valueGetter: (_params: BasicInfo, row) => {
          const actions = row.actionInfo.actionTodo;
          return actions.map((action) => {
            return actionOptions.find((option) => option.value === action)
              ?.displayValue;
          });
        },
        renderCell: (params) => {
          const actions = params.row.actionInfo.actionTodo;

          return (
            <Stack
              flexDirection="row"
              flexWrap="wrap"
              gap={0.5}
              sx={{ py: 0.5 }}
            >
              {actions.map((action) => {
                return (
                  <Chip
                    key={action}
                    label={
                      actionOptions.find((option) => option.value === action)
                        ?.displayValue
                    }
                    size="small"
                    sx={{ width: 'fit-content' }}
                  />
                );
              })}
            </Stack>
          );
        },
      },
    ];

    baseColumns.push({
      field: 'lastModifiedDate',
      headerName: 'Last Modified Date',
      width: 180,
      type: 'date',
      display: 'flex',
      valueGetter: (_params, row) => {
        return new Date(
          row.basicInfo.lastModifiedDate || row.basicInfo.createdDate
        );
      },
      valueFormatter: (params: Date) => {
        return params.toLocaleString();
      },
    });

    // Add remaining columns
    baseColumns.push(
      {
        field: 'creator',
        headerName: 'Creator',
        flex: 1,
        display: 'flex',

        valueGetter: (_params: BasicInfo, row) => {
          return row.basicInfo.creator;
        },
        renderCell: (params) => {
          if (params.value) {
            return <AvatarChip email={params.value} type="User" />;
          } else {
            return <Typography>No Creator</Typography>;
          }
        },
      },
      {
        field: 'totalExecutions',
        headerName: 'Total Executions',
        width: 150,
        display: 'flex',
        renderCell: (params) => {
          if (params.row.basicInfo.status === 'Draft')
            return <Typography>0</Typography>;

          if (isPendingCounts) {
            return <Skeleton width={40} />;
          }

          return (
            <Typography>
              {countsData?.[Number(params.row.id)]?.total ?? '0'}
            </Typography>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: [
          { label: 'disabled', value: 'Disabled' },
          { label: 'enabled', value: 'Enabled' },
          { label: 'draft', value: 'Draft' },
        ],
        valueGetter: (_params: BasicInfo, row) => {
          return row.basicInfo.status;
        },
        renderCell: (params) => {
          const status =
            (params.row.basicInfo.status?.toUpperCase() as StatusType) ?? null;
          return status ? <StatusLabel status={status} /> : null;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        renderCell: (params) => {
          const canViewLogs = isAbleToSeeUIElement(
            rulesEngineRoles,
            RulesEngineRoles.ViewRulesDetails.value
          );
          const canEditRules = isAbleToSeeUIElement(
            rulesEngineRoles,
            RulesEngineRoles.EditRules.value
          );
          const canCloneRules = isAbleToSeeUIElement(
            rulesEngineRoles,
            RulesEngineRoles.AddRules.value
          );
          const canDeleteRules = isAbleToSeeUIElement(
            rulesEngineRoles,
            RulesEngineRoles.DeleteRules.value
          );

          const logsDisabledReason = canViewLogs
            ? undefined
            : Messages.Errors.Permission.Acting(
                RulesEngineRoles.ViewRulesDetails.name
              );
          const editDisabledReason = canEditRules
            ? undefined
            : Messages.Errors.Permission.Acting(
                RulesEngineRoles.EditRules.name
              );
          const cloneDisabledReason = canCloneRules
            ? undefined
            : Messages.Errors.Permission.Acting(RulesEngineRoles.AddRules.name);
          const deleteDisabledReason = canDeleteRules
            ? undefined
            : Messages.Errors.Permission.Acting(
                RulesEngineRoles.DeleteRules.name
              );

          return (
            <GridActionsCell {...params}>
              <GridActionsCellItem
                label="Logs"
                icon={<History />}
                onClick={() => handleOpenLogs(params.row)}
                showInMenu
                disabled={!canViewLogs}
                data-disabled-reason={logsDisabledReason}
              />
              <GridActionsCellItem
                label="Edit"
                icon={<Edit />}
                onClick={() => handleEditRule(params.row)}
                showInMenu
                disabled={!canEditRules}
                data-disabled-reason={editDisabledReason}
              />
              <GridActionsCellItem
                label="Clone"
                icon={<LibraryAdd />}
                onClick={() => handleCloneRule(params.row)}
                showInMenu
                disabled={!canCloneRules}
                data-disabled-reason={cloneDisabledReason}
              />
              <GridActionsCellItem
                label="Delete"
                icon={<Delete />}
                onClick={() => handleDeleteRule(params.row)}
                showInMenu
                disabled={!canDeleteRules}
                data-disabled-reason={deleteDisabledReason}
              />
            </GridActionsCell>
          );
        },
      }
    );

    return baseColumns;
  }, [
    rulesEngineRoles,
    handleCloneRule,
    handleDeleteRule,
    countsData,
    handleEditRule,
    handleOpenLogs,
  ]);

  const someAreEnabled = selectedRulesFromGrid.some(
    (rule) => rule.basicInfo.status === 'Enabled'
  );
  const disabledOrDraftRules = selectedRulesFromGrid.some(
    (rule) =>
      rule.basicInfo.status === 'Disabled' || rule.basicInfo.status === 'Draft'
  );
  const someAreDraft = selectedRulesFromGrid.some(
    (rule) => rule.basicInfo.status === 'Draft'
  );
  function CustomToolbar() {
    const canAddRules = isAbleToSeeUIElement(
      rulesEngineRoles,
      RulesEngineRoles.AddRules.value
    );
    const addRuleTooltip = canAddRules
      ? ''
      : Messages.Errors.Permission.Acting(RulesEngineRoles.AddRules.name);
    return (
      <SharedToolbar
        primaryButtons={
          <CreateRuleDropdownButton
            disabled={!canAddRules}
            tooltipText={addRuleTooltip}
            navigate={navigate}
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="history"
              onClick={() => {
                setOpenActionLogsDialog(true);
              }}
              tooltipLabel="All Action Logs"
            />
            <SharedIconButton
              variant="check"
              onClick={() => handleEnableRule()}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  rulesEngineRoles,
                  RulesEngineRoles.ChangeRuleStatus.value
                ) ||
                someAreEnabled ||
                someAreDraft
              }
              tooltipLabel={
                selectedRows.length === 0
                  ? 'Select rows to enable'
                  : !isAbleToSeeUIElement(
                      rulesEngineRoles,
                      RulesEngineRoles.ChangeRuleStatus.value
                    )
                  ? `You don't have the "Change Rule Status" permission`
                  : someAreEnabled
                  ? 'Some rules are already enabled'
                  : someAreDraft
                  ? 'Complete rule configuration to enable draft rule'
                  : 'Enable'
              }
            />
            <SharedIconButton
              variant="close"
              onClick={() => handleDisableRule()}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  rulesEngineRoles,
                  RulesEngineRoles.ChangeRuleStatus.value
                ) ||
                disabledOrDraftRules
              }
              tooltipLabel={
                selectedRows.length === 0
                  ? 'Select rows to disable'
                  : !isAbleToSeeUIElement(
                      rulesEngineRoles,
                      RulesEngineRoles.ChangeRuleStatus.value
                    )
                  ? `You don't have the "Change Rule Status" permission`
                  : disabledOrDraftRules
                  ? 'Some rules are already disabled'
                  : 'Disable'
              }
            />
            <SharedIconButton
              variant="delete"
              onClick={() => handleDeleteRule()}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  rulesEngineRoles,
                  RulesEngineRoles.DeleteRules.value
                )
              }
              tooltipLabel={
                selectedRows.length === 0
                  ? 'Select rows to delete'
                  : !isAbleToSeeUIElement(
                      rulesEngineRoles,
                      RulesEngineRoles.DeleteRules.value
                    )
                  ? `You don't have the "Delete Rules" permission`
                  : 'Delete'
              }
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [RulesKeys.RulesList],
                });
                void queryClient.invalidateQueries({
                  queryKey: [RulesKeys.RunLogActions],
                });
                void queryClient.invalidateQueries({
                  queryKey: [RulesKeys.RunLogs],
                });
              }}
            />
            <SharedGridStateIconButton
              gridStateKey="rulesEnginesGridStates"
              module={ModuleList.modules.automation.name}
              section={ModuleList.modules.automation.sections.rulesEngine}
            />
          </>
        }
      />
    );
  }

  const isAbleToViewDetails = isAbleToSeeUIElement(
    rulesEngineRoles,
    RulesEngineRoles.ViewRulesDetails.value
  );

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'createdDate',
          sort: 'desc',
        },
      ],
    },
  };

  return (
    <>
      <SharedDataGridClient
        getRowHeight={() => 'auto'}
        gridKey={viewType.rulesEnginesGridStates?.activeGridState || 'default'}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2,
              }}
            >
              <AutoFixHigh sx={{ fontSize: 40 }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontSize: '1.2rem' }}
              >
                Get started with a rule
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    navigate(
                      `/${pagesConfig.rulesEngine.link}/${pagesConfig.createRule.link}`
                    )
                  }
                  disabled={
                    !isAbleToSeeUIElement(
                      rulesEngineRoles,
                      RulesEngineRoles.AddRules.value
                    )
                  }
                >
                  CREATE RULE
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    navigate(
                      `/${pagesConfig.rulesEngine.link}/${pagesConfig.browseTemplates.link}`
                    )
                  }
                  disabled={
                    !isAbleToSeeUIElement(
                      rulesEngineRoles,
                      RulesEngineRoles.AddRules.value
                    )
                  }
                >
                  BROWSE TEMPLATES
                </Button>
              </Stack>
            </Box>
          ),
          noResultsOverlay: createGridOverlays('Rules', <Settings />)
            .noResultsOverlay,

          baseMenuItem: DisabledTooltipMenuItem,
        }}
        checkboxSelection
        rows={data || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        loading={isLoading || isFetching}
        gridStateKey="rulesEnginesGridStates"
        defaultGridState={defaultGridState}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick
      />

      {selectedRuleForDrawer &&
        (uiNewFeatureEnabled ? (
          <RulesEngineDrawerDetails
            selectedRule={selectedRuleForDrawer}
            allPolicies={allPolicies}
            rules={data || []}
            onClose={handleCloseDrawer}
            onEnable={handleEnableRule}
            onDisable={handleDisableRule}
            onDelete={handleDeleteRule}
            onEdit={handleEditRule}
            executionCountsData={
              isPendingCounts
                ? undefined
                : countsData[Number(selectedRuleForDrawer.id)]
            }
            openRulesDetailsDrawer={openRulesDetailsDrawer}
            onClone={handleCloneRule}
            isLoading={isLoading || isLoadingPolicies}
          />
        ) : (
          selectedRuleFromUrl && (
            <Drawer
              anchor="right"
              open={openRulesDetailsDrawer}
              onClose={handleCloseDrawer}
              sx={{
                '& .MuiDrawer-paper': {
                  width: {
                    xs: '100vw',
                    md: '45%',
                  },
                },
              }}
            >
              {(isLoading || isLoadingPolicies) && (
                <Stack
                  sx={{ width: '100%', height: '100%' }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircularProgress />
                </Stack>
              )}
              <RulesEngineDrawerDetailsOld
                selectedRule={selectedRuleFromUrl}
                allPolicies={allPolicies}
                rules={data || []}
                onClose={handleCloseDrawer}
                onEnable={handleEnableRule}
                onDisable={handleDisableRule}
                onDelete={handleDeleteRule}
                onEdit={handleEditRule}
                executionCountsData={
                  isPendingCounts
                    ? undefined
                    : countsData[Number(selectedRuleFromUrl.id)]
                }
                onClone={handleCloneRule}
              />
            </Drawer>
          )
        ))}
      {openDeleteRuleDialog &&
      (ruleForDialog || selectedRulesFromGrid.length > 0) ? (
        <DeleteRuleDialog
          open={openDeleteRuleDialog}
          handleClose={() => {
            setOpenDeleteRuleDialog(false);
            setRuleForDialog(null);
          }}
          changeLogRoles={changeLogRoles}
          selectedRules={
            ruleForDialog ? [ruleForDialog] : selectedRulesFromGrid
          }
          setSelectedRows={setSelectedRows}
          onSuccess={() => {
            const actedUponIds = (
              ruleForDialog ? [ruleForDialog] : selectedRulesFromGrid || []
            ).map((r) => r.id);
            queryClient.setQueryData(
              [RulesKeys.RulesList],
              (oldData: RuleModel[] | undefined) =>
                oldData
                  ? oldData.filter((r) => !actedUponIds.includes(r.id))
                  : oldData
            );
            if (
              selectedRuleFromUrl &&
              actedUponIds.includes(selectedRuleFromUrl.id)
            ) {
              handleCloseDrawer();
            }
          }}
        />
      ) : null}
      {openDisableRuleDialog &&
      (ruleForDialog || selectedRulesFromGrid.length > 0) ? (
        <DisableRuleDialog
          open={openDisableRuleDialog}
          handleClose={() => {
            setOpenDisableRuleDialog(false);
            setRuleForDialog(null);
          }}
          changeLogRoles={changeLogRoles}
          selectedRules={
            ruleForDialog ? [ruleForDialog] : selectedRulesFromGrid
          }
          setSelectedRows={setSelectedRows}
          onSuccess={() => {
            const actedUponIds = (
              ruleForDialog ? [ruleForDialog] : selectedRulesFromGrid || []
            ).map((r) => r.id);
            queryClient.setQueryData(
              [RulesKeys.RulesList],
              (oldData: RuleModel[] | undefined) =>
                oldData
                  ? oldData.map((r) =>
                      actedUponIds.includes(r.id)
                        ? {
                            ...r,
                            basicInfo: { ...r.basicInfo, status: 'Disabled' },
                          }
                        : r
                    )
                  : oldData
            );
            if (
              selectedRuleFromUrl &&
              actedUponIds.includes(selectedRuleFromUrl.id)
            ) {
              updateSelectedRuleStatus('Disabled');
            }
          }}
        />
      ) : null}
      {openEnableRuleDialog &&
      (ruleForDialog || selectedRulesFromGrid.length > 0) ? (
        <EnableRuleDialog
          open={openEnableRuleDialog}
          handleClose={() => {
            setOpenEnableRuleDialog(false);
            setRuleForDialog(null);
          }}
          changeLogRoles={changeLogRoles}
          selectedRules={
            ruleForDialog ? [ruleForDialog] : selectedRulesFromGrid
          }
          setSelectedRows={setSelectedRows}
          onSuccess={() => {
            const actedUponIds = (
              ruleForDialog ? [ruleForDialog] : selectedRulesFromGrid || []
            ).map((r) => r.id);
            queryClient.setQueryData(
              [RulesKeys.RulesList],
              (oldData: RuleModel[] | undefined) =>
                oldData
                  ? oldData.map((r) =>
                      actedUponIds.includes(r.id)
                        ? {
                            ...r,
                            basicInfo: { ...r.basicInfo, status: 'Enabled' },
                          }
                        : r
                    )
                  : oldData
            );
            if (
              selectedRuleFromUrl &&
              actedUponIds.includes(selectedRuleFromUrl.id)
            ) {
              updateSelectedRuleStatus('Enabled');
            }
          }}
        />
      ) : null}
      {openRuleLogsDialog && ruleForDialog ? (
        <RulesEngineLogsDialog
          open={openRuleLogsDialog}
          handleClose={() => {
            setOpenRuleLogsDialog(false);
            setRuleForDialog(null);
          }}
          selectedRule={ruleForDialog}
          policies={allPolicies || []}
          rules={data || []}
        />
      ) : null}
      {openActionLogsDialog ? (
        <RulesEngineAllActionLogsDialog
          open={openActionLogsDialog}
          handleClose={() => setOpenActionLogsDialog(false)}
          policies={allPolicies || []}
          rules={data || []}
        />
      ) : null}
      {openEditRuleDialog && ruleForDialog && (
        <EditRuleDialog
          open={openEditRuleDialog}
          handleClose={() => {
            setOpenEditRuleDialog(false);
            setRuleForDialog(null);
          }}
          rule={ruleForDialog}
          onEditExisting={onEditExisting}
          onCreateNew={() => {
            navigate(
              `/${pagesConfig.rulesEngine.link}/${pagesConfig.createRule.link}`
            );
            setOpenEditRuleDialog(false);
          }}
        />
      )}
    </>
  );
}
